import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Minus,
  Search,
  AlertTriangle,
  Users,
  MessageCircle,
  FileText,
  Mail,
  ExternalLink,
  ChevronRight,
  BarChart3,
  Globe,
  Target,
  Zap,
  RefreshCw,
  Loader2,
  Database,
  Sparkles,
  Bot,
  Award,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ScrollArea } from '../components/ui/scroll-area';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tabs configuration
const SEO_TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'live-tracker', label: 'Live Tracker', icon: Target },
  { id: 'rank-history', label: 'Rank History', icon: TrendingUp },
  { id: 'keywords', label: 'Keywords', icon: Search },
  { id: 'audit', label: 'Site Audit', icon: AlertTriangle },
  { id: 'competitors', label: 'Competitors', icon: Users },
  { id: 'ai-visibility', label: 'AI Visibility', icon: Sparkles },
  { id: 'reddit', label: 'Reddit Questions', icon: MessageCircle },
  { id: 'blog', label: 'Blog Suggestions', icon: FileText },
  { id: 'email', label: 'Email Outreach', icon: Mail },
];

const SEODashboard = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [overviewData, setOverviewData] = useState(null);
  const [keywordsData, setKeywordsData] = useState([]);
  const [auditData, setAuditData] = useState(null);
  const [competitorsData, setCompetitorsData] = useState([]);
  const [redditData, setRedditData] = useState([]);
  const [redditIsLive, setRedditIsLive] = useState(false);
  const [redditScrapedAt, setRedditScrapedAt] = useState(null);
  const [isRefreshingReddit, setIsRefreshingReddit] = useState(false);
  const [blogSuggestions, setBlogSuggestions] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Live Tracker State
  const [quota, setQuota] = useState(null);
  const [liveKeyword, setLiveKeyword] = useState('');
  const [liveResults, setLiveResults] = useState([]);
  const [cachedKeywords, setCachedKeywords] = useState([]);
  const [cachedKeywordsFull, setCachedKeywordsFull] = useState([]);
  const [liveBlogIdeas, setLiveBlogIdeas] = useState([]);
  const [isTracking, setIsTracking] = useState(false);
  
  // Rank History State
  const [trackedKeywordsHistory, setTrackedKeywordsHistory] = useState([]);
  const [selectedKeywordHistory, setSelectedKeywordHistory] = useState(null);
  const [keywordHistoryData, setKeywordHistoryData] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // AI Visibility State
  const [aiVisibilityRun, setAiVisibilityRun] = useState(null);
  const [aiVisibilityBrand, setAiVisibilityBrand] = useState({ brand: 'Stryde Research', domain: 'stryderesearch.com' });
  const [isRunningAEO, setIsRunningAEO] = useState(false);
  const [aeoLoadedOnce, setAeoLoadedOnce] = useState(false);
  
  // Determine if we have live data
  const hasLiveData = cachedKeywords.length > 0 || redditIsLive;

  useEffect(() => {
    fetchAllData();
    fetchQuota();
    fetchCachedKeywords();
    fetchLiveData();
    fetchTrackedKeywordsHistory();
  }, []);

  const fetchQuota = async () => {
    try {
      const response = await axios.get(`${API}/seo/quota`);
      setQuota(response.data);
    } catch (error) {
      console.error('Error fetching quota:', error);
    }
  };

  const fetchCachedKeywords = async () => {
    try {
      const response = await axios.get(`${API}/seo/cached-keywords`);
      setCachedKeywords(response.data.keywords || []);
    } catch (error) {
      console.error('Error fetching cached keywords:', error);
    }
  };

  const fetchLiveData = async () => {
    try {
      const [fullKeywords, blogIdeas] = await Promise.all([
        axios.get(`${API}/seo/cached-keywords-full`),
        axios.get(`${API}/seo/blog-ideas-live`)
      ]);
      setCachedKeywordsFull(fullKeywords.data.keywords || []);
      setLiveBlogIdeas(blogIdeas.data.suggestions || []);
    } catch (error) {
      console.error('Error fetching live data:', error);
    }
  };

  const fetchTrackedKeywordsHistory = async () => {
    try {
      const response = await axios.get(`${API}/seo/tracked-keywords-history`);
      setTrackedKeywordsHistory(response.data.keywords || []);
    } catch (error) {
      console.error('Error fetching tracked keywords history:', error);
    }
  };

  const fetchKeywordHistory = async (keyword) => {
    setIsLoadingHistory(true);
    setSelectedKeywordHistory(keyword);
    try {
      const response = await axios.get(`${API}/seo/rank-history/${encodeURIComponent(keyword)}`);
      setKeywordHistoryData(response.data);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const trackKeywordFresh = async (keyword) => {
    try {
      await axios.post(`${API}/seo/track-keyword-fresh`, { keyword });
      toast.success('Fresh ranking tracked!');
      fetchTrackedKeywordsHistory();
      fetchQuota();
      if (selectedKeywordHistory === keyword) {
        fetchKeywordHistory(keyword);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to track');
    }
  };

  const trackKeyword = async () => {
    if (!liveKeyword.trim()) {
      toast.error('Please enter a keyword');
      return;
    }
    
    if (quota && !quota.within_quota) {
      toast.error('Monthly API quota exceeded');
      return;
    }
    
    setIsTracking(true);
    try {
      const response = await axios.post(`${API}/seo/track-keyword`, {
        keyword: liveKeyword.trim()
      });
      
      const result = response.data;
      setLiveResults([result, ...liveResults.filter(r => r.keyword !== result.keyword)]);
      setLiveKeyword('');
      fetchQuota();
      fetchCachedKeywords();
      
      toast.success(
        result.cached ? 'Retrieved from cache' : 'Keyword tracked!',
        { description: result.found ? `Position: #${result.position}` : 'Not found in top 100' }
      );
      
      // Refresh live data and history
      fetchLiveData();
      fetchTrackedKeywordsHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to track keyword');
    } finally {
      setIsTracking(false);
    }
  };

  const fetchAllData = async () => {
    setIsLoading(true);
    const calls = [
      ['overview', `${API}/seo/overview`],
      ['keywords', `${API}/seo/keywords`],
      ['competitors', `${API}/seo/competitors`],
      ['reddit', `${API}/seo/reddit-questions`],
      ['blog', `${API}/seo/blog-suggestions`],
      ['emails', `${API}/seo/email-templates`],
    ];
    const settled = await Promise.allSettled(calls.map(([, url]) => axios.get(url)));
    const data = {};
    settled.forEach((s, i) => {
      const key = calls[i][0];
      if (s.status === 'fulfilled') data[key] = s.value.data;
      else console.warn(`SEO fetch failed: ${key}`, s.reason?.message);
    });

    if (data.overview) setOverviewData(data.overview);
    if (data.keywords) setKeywordsData(data.keywords.keywords || []);
    if (data.competitors) setCompetitorsData(data.competitors.competitors || []);
    if (data.reddit) {
      setRedditData(data.reddit.questions || []);
      setRedditIsLive(data.reddit.is_live || false);
      setRedditScrapedAt(data.reddit.scraped_at);
    }
    if (data.blog) setBlogSuggestions(data.blog.suggestions || []);
    if (data.emails) setEmailTemplates(data.emails || []);
    setIsLoading(false);
  };

  const fetchAuditData = async () => {
    try {
      const r = await axios.get(`${API}/seo/audit`, { timeout: 120000 });
      setAuditData(r.data);
    } catch (error) {
      console.warn('audit fetch failed', error?.message);
      toast.error('Failed to load Site Audit. Try the Re-run button.');
    }
  };

  const refreshRedditQuestions = async () => {
    setIsRefreshingReddit(true);
    try {
      const response = await axios.post(`${API}/seo/reddit-questions/refresh`);
      setRedditData(response.data.questions || []);
      setRedditIsLive(true);
      setRedditScrapedAt(response.data.scraped_at);
      toast.success(`Found ${response.data.count} questions from Reddit`);
    } catch (error) {
      toast.error('Failed to refresh Reddit questions');
    } finally {
      setIsRefreshingReddit(false);
    }
  };

  const fetchAIVisibilityLatest = async () => {
    try {
      const response = await axios.get(`${API}/seo/ai-visibility/latest`);
      if (response.data?.brand) {
        setAiVisibilityBrand({ brand: response.data.brand, domain: response.data.domain });
      }
      if (response.data?.run) setAiVisibilityRun(response.data.run);
      setAeoLoadedOnce(true);
    } catch (error) {
      console.error('Error fetching AI Visibility latest:', error);
      setAeoLoadedOnce(true);
    }
  };

  const runAIVisibility = async () => {
    setIsRunningAEO(true);
    try {
      const startResp = await axios.post(`${API}/seo/ai-visibility/run`, {});
      const runId = startResp.data?.id;
      const totalKw = startResp.data?.total || 0;
      if (!runId) throw new Error('No run id returned');
      toast.success(`Analysis started for ${totalKw} keyword${totalKw === 1 ? '' : 's'}…`);

      // Poll until complete or error
      const startedAt = Date.now();
      const MAX_MS = 8 * 60 * 1000; // 8 min hard cap
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 3500));
        try {
          const poll = await axios.get(`${API}/seo/ai-visibility/run/${runId}`);
          const r = poll.data?.run;
          if (r) setAiVisibilityRun(r);
          if (r?.status === 'complete') {
            toast.success('AI Visibility analysis complete');
            break;
          }
          if (r?.status === 'error') {
            toast.error(r.error || 'Analysis failed');
            break;
          }
        } catch (pollErr) {
          // transient poll failure — keep retrying until MAX_MS
          console.warn('poll error', pollErr);
        }
        if (Date.now() - startedAt > MAX_MS) {
          toast.error('Analysis timed out. Try fewer keywords.');
          break;
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start AI Visibility analysis');
    } finally {
      setIsRunningAEO(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'ai-visibility' && !aeoLoadedOnce) {
      fetchAIVisibilityLatest();
    }
    if (activeTab === 'audit' && !auditData) {
      fetchAuditData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const TrendIcon = ({ trend }) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="w-8 h-8 border-2 border-[#0070FF] border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Real metrics derived from your tracked keywords */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Tracked Keywords" value={overviewData?.total_tracked_keywords ?? 0} color="blue" />
              <MetricCard label="Ranking" value={overviewData?.ranking_keywords ?? 0} color="green" />
              <MetricCard label="Top 10" value={overviewData?.top_10 ?? 0} color="purple" />
              <MetricCard
                label="Avg. Position"
                value={overviewData?.average_position ? `#${overviewData.average_position}` : '—'}
                color="orange"
              />
            </div>

            {/* Rank Trend (last 30 days) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading font-bold text-lg">Rank Trend (last 30 days)</h3>
                  <p className="text-xs text-gray-500">Lower bar = better ranking</p>
                </div>
                <div className="text-xs text-gray-500">
                  {overviewData?.last_refresh
                    ? `Updated ${new Date(overviewData.last_refresh).toLocaleString()}`
                    : 'No data yet'}
                </div>
              </div>
              {overviewData?.daily_trend?.length ? (
                <div className="flex items-end gap-2 h-40">
                  {overviewData.daily_trend.map((d, i) => {
                    const maxPos = Math.max(...overviewData.daily_trend.map(x => x.avg_position), 10);
                    const heightPct = ((maxPos - d.avg_position + 1) / maxPos) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-2" title={`${d.date} • avg #${d.avg_position}`}>
                        <div
                          className="w-full bg-gradient-to-t from-[#0070FF] to-[#0070FF]/60 rounded-t-lg"
                          style={{ height: `${Math.max(heightPct, 5)}%` }}
                        />
                        <span className="text-[10px] text-gray-500">{d.date.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-12 text-center text-sm text-gray-400">
                  No daily ranking history yet. Use the Live Tracker to start collecting data.
                </div>
              )}
            </div>

            {/* Recent movers + Quota */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-heading font-bold text-lg mb-3">Recent Movers</h3>
                {overviewData?.recent_movers?.length ? (
                  <div className="space-y-2">
                    {overviewData.recent_movers.map((m, i) => (
                      <div key={i} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2 last:border-0">
                        <span className="font-medium truncate pr-2">{m.keyword}</span>
                        <span className={`flex items-center gap-1 text-xs font-semibold ${m.delta > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {m.delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          #{m.previous} → #{m.current}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">No movers yet — track the same keyword multiple times to see changes.</p>
                )}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-heading font-bold text-lg mb-3">SerpApi Quota (this month)</h3>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-[#0070FF]">{overviewData?.serpapi_quota?.searches ?? 0}</span>
                  <span className="text-sm text-gray-500">/ {overviewData?.serpapi_quota?.limit ?? 1000} searches</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#0070FF]"
                    style={{ width: `${Math.min(100, ((overviewData?.serpapi_quota?.searches ?? 0) / (overviewData?.serpapi_quota?.limit || 1000)) * 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">Resets on the 1st of each month.</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <QuickAction icon={Target} label="Live Tracker" onClick={() => setActiveTab('live-tracker')} />
              <QuickAction icon={Search} label="View Keywords" onClick={() => setActiveTab('keywords')} />
              <QuickAction icon={AlertTriangle} label="Site Audit" onClick={() => setActiveTab('audit')} />
              <QuickAction icon={Sparkles} label="AI Visibility" onClick={() => setActiveTab('ai-visibility')} />
            </div>
          </div>
        );

      case 'live-tracker':
        return (
          <div className="space-y-6">
            {/* API Quota Monitor */}
            {quota && (
              <div className="bg-gradient-to-r from-[#0070FF] to-[#FF7F00] rounded-xl p-6 text-white">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="font-heading font-bold text-lg">SerpApi Usage ({quota.month})</h3>
                    <p className="text-white/80 text-sm">Real-time keyword tracking powered by SerpApi</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{quota.searches_remaining}</div>
                    <div className="text-white/80 text-sm">searches left</div>
                  </div>
                </div>
                <div className="w-full bg-white/20 rounded-full h-3 overflow-hidden">
                  <div 
                    className="bg-white h-full transition-all rounded-full"
                    style={{ width: `${quota.percentage_used}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-sm text-white/80">
                  <span>{quota.searches_used} used</span>
                  <span>{quota.searches_limit} total</span>
                </div>
              </div>
            )}

            {/* Track Keyword Input */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Track Keyword Ranking</h3>
              <p className="text-sm text-gray-500 mb-4">Check where stryderesearch.com ranks for any keyword (cached for 7 days)</p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={liveKeyword}
                  onChange={(e) => setLiveKeyword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && trackKeyword()}
                  placeholder="Enter keyword to track..."
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0070FF]"
                  disabled={isTracking}
                />
                <button
                  onClick={trackKeyword}
                  disabled={isTracking || (quota && !quota.within_quota)}
                  className="px-6 py-2 bg-[#0070FF] text-white rounded-lg font-medium hover:bg-[#0055CC] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  data-testid="track-keyword-btn"
                >
                  {isTracking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Track
                </button>
              </div>
            </div>

            {/* Live Results */}
            {liveResults.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <div>
                    <h3 className="font-heading font-bold">Live Search Results</h3>
                    <p className="text-sm text-gray-500">Results from current session</p>
                  </div>
                </div>
                <div className="divide-y divide-gray-200">
                  {liveResults.map((result, i) => (
                    <div key={i} className="p-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{result.keyword}</span>
                        <div className="flex items-center gap-2">
                          {result.cached && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs flex items-center gap-1">
                              <Database className="w-3 h-3" /> Cached
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            result.found 
                              ? result.position <= 10 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {result.found ? `#${result.position}` : 'Not in Top 100'}
                          </span>
                        </div>
                      </div>
                      {result.related_searches?.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500 mb-1">Related searches:</p>
                          <div className="flex flex-wrap gap-1">
                            {result.related_searches.slice(0, 5).map((s, j) => (
                              <span key={j} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Cached Keywords */}
            {cachedKeywords.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <div>
                    <h3 className="font-heading font-bold">Cached Keywords</h3>
                    <p className="text-sm text-gray-500">{cachedKeywords.length} keywords cached (7-day TTL)</p>
                  </div>
                  <button
                    onClick={fetchCachedKeywords}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <RefreshCw className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Keyword</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Position</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Searched</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {cachedKeywords.map((kw, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-sm">{kw.keyword}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              kw.found
                                ? kw.position <= 10 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {kw.found ? `#${kw.position}` : 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              kw.found ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {kw.found ? 'Found' : 'Not Found'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center text-xs text-gray-500">
                            {kw.searched_at ? new Date(kw.searched_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );

      case 'rank-history':
        return (
          <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#0070FF] to-[#FF7F00] rounded-xl p-6 text-white">
              <h3 className="font-heading font-bold text-xl flex items-center gap-2">
                <TrendingUp className="w-6 h-6" />
                Rank History & Trends
                <span className="px-2 py-0.5 bg-white/20 rounded text-xs ml-auto">1000 searches/mo</span>
              </h3>
              <p className="text-white/80 mt-1">Track keyword rankings over time and spot trends</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tracked Keywords List */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-heading font-bold">Tracked Keywords</h3>
                    <p className="text-sm text-gray-500">{trackedKeywordsHistory.length} keywords with history</p>
                  </div>
                  <div className="max-h-[500px] overflow-y-auto">
                    {trackedKeywordsHistory.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        <Search className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No historical data yet.</p>
                        <p className="text-xs mt-1">Track keywords in Live Tracker to start collecting history.</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {trackedKeywordsHistory.map((kw, i) => (
                          <button
                            key={i}
                            onClick={() => fetchKeywordHistory(kw.keyword)}
                            className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                              selectedKeywordHistory === kw.keyword ? 'bg-blue-50 border-l-2 border-[#0070FF]' : ''
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-sm truncate">{kw.keyword}</span>
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                kw.found 
                                  ? kw.latest_position <= 10 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {kw.found ? `#${kw.latest_position}` : 'N/A'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                              {kw.data_points} data points • Last: {kw.latest_date}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Rank History Chart & Details */}
              <div className="lg:col-span-2">
                {isLoadingHistory ? (
                  <div className="bg-white rounded-xl border border-gray-200 p-8 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-[#0070FF]" />
                  </div>
                ) : keywordHistoryData ? (
                  <div className="space-y-4">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <p className="text-xs text-gray-500">Current Position</p>
                        <p className="text-2xl font-bold text-[#0070FF]">
                          {keywordHistoryData.current_position ? `#${keywordHistoryData.current_position}` : 'N/A'}
                        </p>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <p className="text-xs text-gray-500">Best Position</p>
                        <p className="text-2xl font-bold text-green-600">
                          {keywordHistoryData.best_position ? `#${keywordHistoryData.best_position}` : 'N/A'}
                        </p>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <p className="text-xs text-gray-500">Trend</p>
                        <p className={`text-lg font-bold flex items-center gap-1 ${
                          keywordHistoryData.trend === 'improving' ? 'text-green-600' :
                          keywordHistoryData.trend === 'declining' ? 'text-red-600' : 'text-gray-600'
                        }`}>
                          {keywordHistoryData.trend === 'improving' && <TrendingUp className="w-5 h-5" />}
                          {keywordHistoryData.trend === 'declining' && <TrendingDown className="w-5 h-5" />}
                          {keywordHistoryData.trend === 'stable' && <Minus className="w-5 h-5" />}
                          {keywordHistoryData.trend?.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="bg-white rounded-xl border border-gray-200 p-4">
                        <p className="text-xs text-gray-500">Data Points</p>
                        <p className="text-2xl font-bold text-gray-700">{keywordHistoryData.data_points}</p>
                      </div>
                    </div>

                    {/* Chart */}
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-heading font-bold">Position History</h4>
                        <button
                          onClick={() => trackKeywordFresh(selectedKeywordHistory)}
                          className="px-3 py-1.5 bg-[#0070FF] text-white rounded-lg text-sm font-medium hover:bg-[#0055CC] flex items-center gap-2"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Track Fresh
                        </button>
                      </div>
                      
                      {keywordHistoryData.history?.length > 0 ? (
                        <div className="h-48">
                          <div className="flex items-end justify-between gap-1 h-full">
                            {keywordHistoryData.history.map((h, i) => {
                              const pos = h.found ? h.position : 100;
                              const height = Math.max(5, 100 - pos);
                              return (
                                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                  <div className="text-xs text-gray-500">
                                    {h.found ? `#${h.position}` : '-'}
                                  </div>
                                  <div 
                                    className={`w-full rounded-t transition-all ${
                                      h.found 
                                        ? h.position <= 10 ? 'bg-green-500' : h.position <= 30 ? 'bg-yellow-500' : 'bg-orange-500'
                                        : 'bg-gray-300'
                                    }`}
                                    style={{ height: `${height}%` }}
                                    title={`${h.date}: Position ${h.found ? h.position : 'Not found'}`}
                                  />
                                  <div className="text-[10px] text-gray-400 -rotate-45 origin-top-left">
                                    {h.date?.slice(5)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="h-48 flex items-center justify-center text-gray-400">
                          <p>No history data available</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">Select a keyword to view its ranking history</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'keywords':
        return (
          <div className="space-y-6">
            {/* Live Tracked Keywords */}
            {cachedKeywordsFull.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                  <div>
                    <h3 className="font-heading font-bold flex items-center gap-2">
                      Live Tracked Keywords
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">LIVE</span>
                    </h3>
                    <p className="text-sm text-gray-500">Real rankings from SerpApi ({cachedKeywordsFull.length} keywords)</p>
                  </div>
                  <button
                    onClick={() => setActiveTab('live-tracker')}
                    className="px-3 py-1.5 bg-[#0070FF] text-white rounded-lg text-sm font-medium hover:bg-[#0055CC]"
                  >
                    + Track More
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Keyword</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Position</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Related Searches</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {cachedKeywordsFull.map((kw, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-sm">{kw.keyword}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              kw.found
                                ? kw.position <= 10 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {kw.found ? `#${kw.position}` : 'Not in Top 100'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              kw.found ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {kw.found ? 'Ranking' : 'Not Found'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {kw.related_searches?.slice(0, 3).map((s, j) => (
                                <span key={j} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{s}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Mock Keywords (for reference) */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="font-heading font-bold flex items-center gap-2">
                    Sample Keywords
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">SAMPLE</span>
                  </h3>
                  <p className="text-sm text-gray-500">Example data - Track real keywords in Live Tracker</p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Keyword</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Position</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Volume</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Difficulty</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {keywordsData.map((kw, i) => (
                      <tr key={i} className="hover:bg-gray-50 opacity-60">
                        <td className="px-4 py-3 font-medium text-sm">{kw.keyword}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            kw.position <= 3 ? 'bg-green-100 text-green-700' :
                            kw.position <= 10 ? 'bg-blue-100 text-blue-700' :
                            kw.position <= 20 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            #{kw.position}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600">{kw.volume.toLocaleString()}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${
                                  kw.difficulty < 30 ? 'bg-green-500' :
                                  kw.difficulty < 50 ? 'bg-yellow-500' :
                                  kw.difficulty < 70 ? 'bg-orange-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${kw.difficulty}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500">{kw.difficulty}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <TrendIcon trend={kw.trend} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case 'audit':
        return (
          <div className="space-y-6">
            {/* Score */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="font-heading font-bold text-lg">Site Health Score</h3>
                  <p className="text-sm text-gray-500">
                    Based on a real crawl of {auditData?.domain || 'stryderesearch.com'} ({auditData?.pages_audited ?? 0} pages)
                    {auditData?.audited_at && ` • last audited ${new Date(auditData.audited_at).toLocaleString()}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={async () => {
                      try {
                        toast.message('Re-running audit…');
                        const r = await axios.get(`${API}/seo/audit?refresh=true`, { timeout: 120000 });
                        setAuditData(r.data);
                        toast.success('Audit refreshed');
                      } catch {
                        toast.error('Audit refresh failed');
                      }
                    }}
                    data-testid="audit-refresh-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <RefreshCw className="w-4 h-4" /> Re-run audit
                  </button>
                  <div className="relative w-24 h-24">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle cx="48" cy="48" r="40" stroke="#E5E7EB" strokeWidth="8" fill="none" />
                      <circle
                        cx="48" cy="48" r="40"
                        stroke={auditData?.score >= 80 ? '#22C55E' : auditData?.score >= 60 ? '#F59E0B' : '#EF4444'}
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${((auditData?.score ?? 0) / 100) * 251} 251`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold">{auditData?.score ?? '—'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Issues */}
            <div className="grid md:grid-cols-3 gap-4">
              <IssueCard
                title="Critical Issues"
                count={auditData?.issues?.critical?.length || 0}
                color="red"
                issues={auditData?.issues?.critical}
              />
              <IssueCard
                title="Warnings"
                count={auditData?.issues?.warnings?.length || 0}
                color="yellow"
                issues={auditData?.issues?.warnings}
              />
              <IssueCard
                title="Notices"
                count={auditData?.issues?.notices?.length || 0}
                color="blue"
                issues={auditData?.issues?.notices}
              />
            </div>

            {/* Crawl checks */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Technical Checks</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <CheckRow label="robots.txt" ok={!!auditData?.checks?.robots_txt} />
                <CheckRow label="sitemap.xml" ok={!!auditData?.checks?.sitemap_xml} />
                <CheckRow
                  label="Broken internal links"
                  ok={(auditData?.checks?.broken_internal_links ?? 0) === 0}
                  detail={auditData?.checks?.broken_internal_links ? `${auditData.checks.broken_internal_links} found` : 'none'}
                />
              </div>
            </div>

            {/* Performance (Google PageSpeed Insights) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-lg">Performance (PageSpeed Insights)</h3>
                {!auditData?.performance?.page_speed_mobile && !auditData?.performance?.page_speed_desktop && (
                  <span className="text-xs text-gray-400">PageSpeed temporarily unavailable</span>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{auditData?.performance?.page_speed_desktop ?? '—'}</div>
                  <div className="text-sm text-gray-500">Desktop Speed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{auditData?.performance?.page_speed_mobile ?? '—'}</div>
                  <div className="text-sm text-gray-500">Mobile Speed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {auditData?.performance?.core_web_vitals?.lcp != null ? `${auditData.performance.core_web_vitals.lcp}s` : '—'}
                  </div>
                  <div className="text-sm text-gray-500">LCP</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {auditData?.performance?.core_web_vitals?.cls != null ? auditData.performance.core_web_vitals.cls : '—'}
                  </div>
                  <div className="text-sm text-gray-500">CLS</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'competitors':
        return (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="competitors-tab">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-heading font-bold">Competitor Analysis</h3>
              <p className="text-sm text-gray-500">
                Domains showing up most often in your tracked keywords' SERPs.
                Based on cached SerpApi top-10 results across {competitorsData?.length ? overviewData?.total_tracked_keywords ?? '—' : 0} keyword(s).
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Competitor</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Keyword Overlap</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Avg. Position</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Sample Keywords</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {competitorsData?.length ? competitorsData.map((comp, i) => (
                    <tr key={i} className="hover:bg-gray-50" data-testid={`competitor-row-${i}`}>
                      <td className="px-4 py-3">
                        <a
                          href={`https://${comp.domain}`}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-[#0070FF] hover:underline inline-flex items-center gap-1"
                        >
                          {comp.domain}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                          {comp.keyword_count} keyword{comp.keyword_count === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm font-medium">
                        {comp.average_position ? `#${comp.average_position}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        {(comp.sample_keywords || []).slice(0, 3).join(', ') || '—'}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-400">
                        No competitor data yet. Track keywords in the Live Tracker to populate this table.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'reddit':
        return (
          <div className="space-y-4">
            <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-4 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-heading font-bold flex items-center gap-2">
                    Reddit Question Finder
                    {redditIsLive && (
                      <span className="px-2 py-0.5 bg-white/20 rounded text-xs">LIVE</span>
                    )}
                  </h3>
                  <p className="text-sm text-white/80">
                    {redditIsLive 
                      ? `Real questions scraped from Reddit • ${redditData.length} found`
                      : 'Questions people are asking about clinical research'
                    }
                  </p>
                  {redditScrapedAt && (
                    <p className="text-xs text-white/60 mt-1">
                      Last updated: {new Date(redditScrapedAt).toLocaleString()}
                    </p>
                  )}
                </div>
                <button
                  onClick={refreshRedditQuestions}
                  disabled={isRefreshingReddit}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                >
                  {isRefreshingReddit ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Refresh
                </button>
              </div>
            </div>
            {redditData.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No questions found. Click Refresh to scrape Reddit.</p>
              </div>
            ) : (
              redditData.map((q, i) => (
                <motion.div
                  key={i}
                  className={`bg-white rounded-xl border p-4 hover:shadow-md transition-shadow ${
                    redditIsLive ? 'border-orange-200' : 'border-gray-200'
                  }`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{q.question}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">
                          r/{q.subreddit}
                        </span>
                        <span>{q.upvotes} upvotes</span>
                        <span>{q.comments} comments</span>
                      </div>
                    </div>
                    <a 
                      href={q.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 text-gray-400" />
                    </a>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        );

      case 'blog':
        return (
          <div className="space-y-4">
            {/* Live Blog Ideas from SerpApi */}
            {liveBlogIdeas.length > 0 && (
              <>
                <div className="bg-gradient-to-r from-[#0070FF] to-[#FF7F00] rounded-xl p-4 text-white">
                  <h3 className="font-heading font-bold flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Live Blog Ideas from Search Data
                    <span className="px-2 py-0.5 bg-white/20 rounded text-xs ml-auto">LIVE</span>
                  </h3>
                  <p className="text-sm text-white/80">Generated from "People Also Ask" and related searches</p>
                </div>
                {liveBlogIdeas.map((idea, i) => (
                  <motion.div
                    key={`live-${i}`}
                    className="bg-white rounded-xl border-2 border-[#0070FF]/30 p-4 hover:border-[#0070FF] transition-colors"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <h4 className="font-heading font-bold text-lg text-gray-900">{idea.title}</h4>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                        {idea.source}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                        {idea.type}
                      </span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs">
                        From: {idea.source_keyword}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        idea.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {idea.priority} Priority
                      </span>
                    </div>
                  </motion.div>
                ))}
              </>
            )}

            {/* Sample Blog Ideas */}
            <div className="bg-gradient-to-r from-gray-500 to-gray-600 rounded-xl p-4 text-white">
              <h3 className="font-heading font-bold flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Sample Blog Suggestions
                <span className="px-2 py-0.5 bg-white/20 rounded text-xs ml-auto">SAMPLE</span>
              </h3>
              <p className="text-sm text-white/80">Example ideas - Track keywords to generate real suggestions</p>
            </div>
            {blogSuggestions.map((suggestion, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-xl border border-gray-200 p-4 hover:border-[#0070FF] transition-colors opacity-60"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 0.6, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <h4 className="font-heading font-bold text-lg text-gray-900">{suggestion.title}</h4>
                <p className="text-sm text-gray-600 mt-2">{suggestion.content_angle}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                    Keyword: {suggestion.target_keyword}
                  </span>
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                    Vol: {suggestion.search_volume}
                  </span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                    Diff: {suggestion.difficulty}
                  </span>
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                    Reddit: {suggestion.reddit_interest}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        );

      case 'email':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-heading font-bold text-lg mb-4">Backlink Outreach Templates</h3>
              <p className="text-sm text-gray-500 mb-4">Pre-built email templates for link building outreach</p>
              
              {/* Default Templates */}
              <div className="space-y-3">
                <EmailTemplateCard
                  name="Guest Post Pitch"
                  subject="Guest Post Opportunity for [Site Name]"
                  preview="Hi [Name], I've been following your content on [Topic] and would love to contribute..."
                />
                <EmailTemplateCard
                  name="Resource Page Request"
                  subject="Suggestion for Your [Topic] Resources Page"
                  preview="Hello, I noticed your excellent resource page on [Topic]. We recently published..."
                />
                <EmailTemplateCard
                  name="Broken Link Replacement"
                  subject="Broken Link on Your [Page Title]"
                  preview="Hi, I was reading your article on [Topic] and noticed a broken link to..."
                />
              </div>
            </div>

            {/* Saved Templates */}
            {emailTemplates.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-heading font-bold text-lg mb-4">Your Saved Templates</h3>
                <div className="space-y-3">
                  {emailTemplates.map((template, i) => (
                    <EmailTemplateCard
                      key={template.id}
                      name={template.name}
                      subject={template.subject}
                      preview={template.body.substring(0, 100) + '...'}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'ai-visibility':
        return renderAIVisibilityTab();

      default:
        return null;
    }
  };

  const renderAIVisibilityTab = () => {
    const run = aiVisibilityRun;
    const summary = run?.summary || {};
    const results = run?.results || [];
    const modelDefs = [
      { key: 'chatgpt', label: 'ChatGPT', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
      { key: 'claude', label: 'Claude', color: 'text-orange-600 bg-orange-50 border-orange-200' },
      { key: 'gemini', label: 'Gemini', color: 'text-sky-600 bg-sky-50 border-sky-200' },
    ];

    const RankBadge = ({ data }) => {
      if (!data) return <span className="text-xs text-gray-400">—</span>;
      if (data.error) return <span className="text-xs text-red-500" title={data.error}>error</span>;
      if (data.rank && data.rank > 0) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
            <Award className="w-3 h-3" /> #{data.rank}
          </span>
        );
      }
      if (data.mentioned) {
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
            <CheckCircle2 className="w-3 h-3" /> Mentioned
          </span>
        );
      }
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
          <AlertCircle className="w-3 h-3" /> Not visible
        </span>
      );
    };

    return (
      <div className="space-y-6" data-testid="ai-visibility-tab">
        {/* Header / Run control */}
        <div className="bg-gradient-to-br from-[#0070FF] to-[#0050CC] text-white rounded-xl p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5" />
                <h2 className="font-heading font-bold text-xl">Answer Engine Optimization</h2>
              </div>
              <p className="text-sm text-white/80 max-w-xl">
                Track how often AI assistants (ChatGPT, Claude, Gemini) recommend{' '}
                <span className="font-semibold">{aiVisibilityBrand.brand}</span> ({aiVisibilityBrand.domain}){' '}
                for your tracked keywords.
              </p>
              {run?.created_at && (
                <p className="text-xs text-white/60 mt-2">
                  Last run: {new Date(run.created_at).toLocaleString()}
                </p>
              )}
            </div>
            <button
              onClick={runAIVisibility}
              disabled={isRunningAEO}
              data-testid="run-ai-visibility-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-[#0070FF] rounded-lg font-semibold text-sm hover:bg-white/90 transition-colors disabled:opacity-60"
            >
              {isRunningAEO ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing keywords…</>
              ) : (
                <><RefreshCw className="w-4 h-4" /> {run ? 'Run Again' : 'Run Analysis'}</>
              )}
            </button>
          </div>
          {isRunningAEO && (
            <p className="text-xs text-white/70 mt-3">
              {aiVisibilityRun?.total
                ? `Analyzing keyword ${Math.min((aiVisibilityRun.progress || 0) + (aiVisibilityRun.status === 'complete' ? 0 : 1), aiVisibilityRun.total)} of ${aiVisibilityRun.total}…`
                : 'Querying GPT-5.2, Claude Sonnet 4.5, and Gemini 2.5 Pro for each tracked keyword. This typically takes 30–90 seconds per keyword.'}
            </p>
          )}
        </div>

        {!run && !isRunningAEO && (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center">
            <Bot className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              No analysis yet. Click <span className="font-semibold">Run Analysis</span> to check whether AI assistants mention your brand for your tracked keywords.
            </p>
          </div>
        )}

        {run && (
          <>
            {/* Summary metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                label="Visibility Score"
                value={summary.visibility_score ?? 0}
                suffix="%"
                color="blue"
              />
              <MetricCard
                label="Mentions"
                value={`${summary.mentions ?? 0} / ${summary.total_checks ?? 0}`}
                color="green"
              />
              <MetricCard
                label="Avg. Rank"
                value={summary.average_rank ? `#${summary.average_rank}` : '—'}
                color="purple"
              />
              <MetricCard
                label="Visible Keywords"
                value={`${summary.visible_keywords?.length ?? 0} / ${results.length}`}
                color="orange"
              />
            </div>

            {/* Per-keyword table */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="font-heading font-bold text-lg">Per-Keyword Visibility</h3>
                <p className="text-xs text-gray-500 mt-1">
                  How each AI assistant ranked or mentioned <span className="font-semibold">{aiVisibilityBrand.brand}</span> when asked about each keyword.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                      <th className="px-6 py-3 font-medium">Keyword</th>
                      {modelDefs.map((m) => (
                        <th key={m.key} className="px-4 py-3 font-medium">{m.label}</th>
                      ))}
                      <th className="px-6 py-3 font-medium">Top Competitors</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {results.map((r, i) => (
                      <tr key={i} data-testid={`aeo-row-${i}`} className="hover:bg-gray-50/60">
                        <td className="px-6 py-3 font-medium text-gray-900">{r.keyword}</td>
                        {modelDefs.map((m) => (
                          <td key={m.key} className="px-4 py-3"><RankBadge data={r.by_model?.[m.key]} /></td>
                        ))}
                        <td className="px-6 py-3 text-xs text-gray-600">
                          {(r.competitors || []).slice(0, 3).map((c) => c.name).join(', ') || '—'}
                        </td>
                      </tr>
                    ))}
                    {results.length === 0 && (
                      <tr><td colSpan={5} className="px-6 py-10 text-center text-gray-400 text-sm">No results yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Top competitors + Recommendations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-heading font-bold text-lg mb-4">Top Competing Brands</h3>
                <p className="text-xs text-gray-500 mb-4">Brands AI assistants recommend instead of yours.</p>
                {(summary.top_competitors || []).length === 0 && (
                  <p className="text-sm text-gray-400">No competitors detected yet.</p>
                )}
                <div className="space-y-2">
                  {(summary.top_competitors || []).map((c, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <span className="text-sm font-medium text-gray-800">{c.name}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100">
                        cited {c.count}×
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#0070FF]" /> AI-Generated Recommendations
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  How to get <span className="font-semibold">{aiVisibilityBrand.brand}</span> mentioned in AI answers.
                </p>
                {summary.recommendations ? (
                  <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap">
                    {summary.recommendations}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Recommendations will appear after a successful run.</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="seo-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="seo-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <img src="/stryde-bird.jpg" alt="Stryde" className="w-8 h-8 object-contain" />
                <div>
                  <h1 className="font-heading text-xl font-bold" style={{color: '#0070FF'}}>SEO Dashboard</h1>
                  <p className="text-sm text-gray-500">stryderesearch.com</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {hasLiveData ? (
                <span className="px-3 py-1 bg-green-500 text-white rounded-full text-xs font-bold flex items-center gap-1">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  LIVE DATA
                </span>
              ) : (
                <span className="px-3 py-1 bg-[#FF7F00] text-white rounded-full text-xs font-bold">
                  SAMPLE DATA
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-56 flex-shrink-0">
            <nav className="bg-white rounded-xl border border-gray-200 p-2 sticky top-24">
              {SEO_TABS.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                      activeTab === tab.id 
                        ? 'bg-[#0070FF] text-white' 
                        : 'hover:bg-gray-100 text-gray-700'
                    }`}
                    data-testid={`seo-tab-${tab.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const MetricCard = ({ label, value, suffix = '', color }) => {
  const colors = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-sm text-gray-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className={`text-2xl font-bold bg-gradient-to-r ${colors[color]} bg-clip-text text-transparent`}>
          {value}
        </span>
        {suffix && <span className="text-sm text-gray-400">{suffix}</span>}
      </div>
    </div>
  );
};

const QuickAction = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="bg-white rounded-xl border border-gray-200 p-4 hover:border-[#0070FF] hover:shadow-md transition-all flex items-center gap-3"
  >
    <div className="w-10 h-10 rounded-full bg-[#0070FF]/10 flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#0070FF]" />
    </div>
    <span className="font-medium text-sm">{label}</span>
    <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
  </button>
);

const IssueCard = ({ title, count, color, issues }) => {
  const colors = {
    red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-500' },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', badge: 'bg-yellow-500' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-500' },
  };

  return (
    <div className={`rounded-xl border ${colors[color].border} ${colors[color].bg} p-4`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className={`font-medium ${colors[color].text}`}>{title}</h4>
        <span className={`${colors[color].badge} text-white text-xs font-bold px-2 py-0.5 rounded-full`}>
          {count}
        </span>
      </div>
      <div className="space-y-2">
        {issues?.slice(0, 2).map((issue, i) => (
          <p key={i} className="text-sm text-gray-600 line-clamp-1">{issue.issue}</p>
        ))}
      </div>
    </div>
  );
};

const EmailTemplateCard = ({ name, subject, preview }) => (
  <div className="border border-gray-200 rounded-lg p-4 hover:border-[#0070FF] transition-colors cursor-pointer">
    <div className="flex items-center justify-between mb-2">
      <span className="font-medium">{name}</span>
      <Mail className="w-4 h-4 text-gray-400" />
    </div>
    <p className="text-sm text-[#0070FF] mb-1">{subject}</p>
    <p className="text-sm text-gray-500 line-clamp-2">{preview}</p>
  </div>
);

const CheckRow = ({ label, ok, detail }) => (
  <div className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
    <span className="text-gray-700">{label}</span>
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${ok ? 'text-green-600' : 'text-red-600'}`}>
      {ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {detail || (ok ? 'OK' : 'Missing')}
    </span>
  </div>
);

export default SEODashboard;
